import { isOptionalZodSchema } from '@erikdakoda/utils/zod';
import React, { useEffect, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import InputLabel from '@mui/material/InputLabel';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import OutlinedInput from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { Controller } from 'react-hook-form';
import startCase from 'lodash/startCase';

import ClipboardButton from '@erikdakoda/ui/components/ClipboardButton';
import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import PowerIconButton from '@erikdakoda/ui/components/PowerIconButton';
import Spinner from '@erikdakoda/ui/components/Spinner';
import { doFetch } from '@erikdakoda/ui/shared/doFetch';
import type { CustomControlProps } from '@erikdakoda/editor-ui/types';
import type { ToolCatalogResponse } from '@erikdakoda/tool-set/api-handlers/toolCatalogTypes';
import type { ToolSetForEdit } from '@erikdakoda/tool-set/types';

import type { ToolSetEntryDraft } from './updateToolSetAndEntries';
import { entriesFromToolSet, resolveEntryDraftsForSave } from './updateToolSetAndEntries';
import { insertEntryInAvailableSourceOrder } from './insertEntryInAvailableSourceOrder';

type ToolSetEditorControlProps = CustomControlProps<ToolSetForEdit>;

type AvailableSource = {
  kind: ToolSetEntryDraft['kind'];
  key: string;
  title: string;
  toolCount: number | null;
  disabled: boolean;
  unavailable?: boolean;
};

function isToolCatalogResponse(data: unknown): data is ToolCatalogResponse {
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as ToolCatalogResponse;
  if (
    !Array.isArray(candidate.mcpServers) ||
    !Array.isArray(candidate.nativeGroups) ||
    typeof candidate.appCapabilities !== 'object' ||
    candidate.appCapabilities === null ||
    typeof candidate.appCapabilities.stdioMcp !== 'boolean'
  ) {
    return false;
  }
  return candidate.mcpServers.every(
    (server) =>
      typeof server.name === 'string' &&
      typeof server.transport === 'string' &&
      Array.isArray(server.tools) &&
      typeof server.discoveryStatus === 'string',
  );
}

function discoveryStatusCaption(
  status: ToolCatalogResponse['mcpServers'][number]['discoveryStatus'],
): string {
  switch (status) {
    case 'cached':
      return 'Tools loaded from database cache (discovered on Forgentic or unchanged version).';
    case 'error':
      return 'Tool discovery failed.';
    case 'unavailable':
      return 'No cached tools yet. Discover tools on Forgentic, then reload here.';
    default:
      return '';
  }
}

/** AI SDK tool key: MCP uses `{server}__{name}`; native entries use the bare tool id. */
function runtimeToolId(entry: ToolSetEntryDraft, shortToolKey: string): string {
  return entry.kind === 'mcp_server' ? `${entry.key}__${shortToolKey}` : shortToolKey;
}

type ToolOverrideRowProps = {
  toolKey: string;
  toolLabel: string;
  entry: ToolSetEntryDraft;
  entryDeferred: boolean;
  onToggleDisabled: (toolKey: string, disabled: boolean) => void;
  onToggleDeferred: (toolKey: string, deferred: boolean) => void;
};

export default function ToolSetEditorControl(props: ToolSetEditorControlProps) {
  'use memo';

  const { item, fieldName, fieldSpec, control, fieldSchema } = props;
  const baselineEntries = entriesFromToolSet(item?.entries);

  const isRequired = fieldSchema ? !isOptionalZodSchema(fieldSchema) : false;

  return (
    <FormControl className="tool-set-editor-control" fullWidth>
      <Controller
        name={fieldName}
        defaultValue={baselineEntries}
        control={control}
        rules={{ required: isRequired }}
        render={({ field: { value, onChange } }) => (
          <Box sx={{ position: 'relative', pt: 1 }}>
            <InputLabel
              variant="outlined"
              shrink
              htmlFor={`tool-set-members-${item?.id ?? fieldName}`}
            >
              {fieldSpec?.label || startCase(fieldName)}
            </InputLabel>
            <OutlinedInput
              id={`tool-set-members-${item?.id ?? fieldName}`}
              label={fieldSpec?.label || startCase(fieldName)}
              notched
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                padding: '14px 16px',
                '& .MuiOutlinedInput-input': { display: 'none' },
              }}
            />
            <ToolSetControlEditor
              entries={resolveEntryDraftsForSave(baselineEntries, value)}
              onChange={onChange}
            />
          </Box>
        )}
      />
    </FormControl>
  );
}

function ToolOverrideRow({
  toolKey,
  toolLabel,
  entry,
  entryDeferred,
  onToggleDisabled,
  onToggleDeferred,
}: ToolOverrideRowProps) {
  'use memo';

  const disabled = entry.disabledTools.includes(toolKey);
  const deferred = entryDeferred || entry.deferredTools.includes(toolKey);
  const toolId = runtimeToolId(entry, toolKey);

  return (
    <Box
      className="tool-override-row"
      component="li"
      sx={(theme) => ({
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
        px: '16px',
        py: 0.5,
        color: disabled ? 'text.disabled' : deferred ? 'warning.main' : 'text.primary',
        '& .hover-item': {
          opacity: 0,
          transition: theme.transitions.create(['opacity'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.standard,
          }),
        },
        '&:hover .hover-item': {
          opacity: 1,
        },
      })}
    >
      <Typography variant="body1" sx={{ color: 'inherit' }}>
        {toolLabel}
      </Typography>
      <Box
        onClick={(event) => {
          event.stopPropagation();
        }}
        sx={{ display: 'inline-flex' }}
      >
        <ClipboardButton className="hover-item" text={toolId} title={`Copy tool ID '${toolId}'`} />
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      <PowerIconButton
        icon={disabled ? 'circle' : 'circle-check'}
        title={disabled ? 'Include' : 'Included'}
        aria-label={`${disabled ? 'Exclude' : 'Include'} ${toolLabel}`}
        size="small"
        padding={3}
        tooltipPlacement="left"
        onClick={(event) => {
          event.stopPropagation();
          onToggleDisabled(toolKey, disabled);
        }}
      />
      <PowerIconButton
        icon="clock"
        title={deferred ? 'Deferred' : 'Defer'}
        aria-label={`Defer ${toolLabel}`}
        size="small"
        disabled={entryDeferred || disabled}
        color={deferred ? 'warning' : 'default'}
        padding={3}
        tooltipPlacement="left"
        onClick={(event) => {
          event.stopPropagation();
          onToggleDeferred(toolKey, deferred);
        }}
        sx={
          disabled
            ? {}
            : {
                '&.MuiIconButton-colorWarning.Mui-disabled': {
                  color: 'warning.main',
                },
              }
        }
      />
    </Box>
  );
}

function getSourceIcon(
  kind: ToolSetEntryDraft['kind'],
  key: string,
  catalog: ToolCatalogResponse | null,
): string {
  if (kind === 'native_group') {
    const group = catalog?.nativeGroups.find((g) => g.key === key);
    return group?.icon ?? 'hammer';
  }

  const server = catalog?.mcpServers.find((s) => s.name === key);
  return server?.icon ?? 'mcp';
}

function formatToolCount(count: number | null): string {
  if (count === null) return '';
  return `${count} tool${count === 1 ? '' : 's'}`;
}

function ToolSetControlEditor({
  entries,
  onChange,
}: {
  entries: ToolSetEntryDraft[];
  onChange: (entries: ToolSetEntryDraft[]) => void;
}) {
  'use memo';

  const { t } = useTranslation('tool-set-ui');
  const [catalog, setCatalog] = useState<ToolCatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  async function loadCatalog(refresh: boolean): Promise<void> {
    setLoadFailed(false);
    if (refresh) setRefreshing(true);
    else setLoading(true);

    try {
      const data = await doFetch<ToolCatalogResponse>({
        url: '/api/admin/tool-catalog',
        params: refresh ? { refresh: 'true' } : {},
      });
      if (isToolCatalogResponse(data)) {
        setCatalog(data);
      } else {
        setLoadFailed(true);
      }
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    void loadCatalog(false);
  }, []);

  const configuredKeys = new Set(entries.map((e) => `${e.kind}:${e.key}`));
  const mcpServers = catalog?.mcpServers ?? [];
  const nativeGroups = catalog?.nativeGroups ?? [];

  const availableSources: AvailableSource[] = nativeGroups.map((group) => ({
    kind: 'native_group' as const,
    key: group.key,
    title: group.label || startCase(group.key),
    toolCount: (group.tools ?? []).length,
    disabled: configuredKeys.has(`native_group:${group.key}`),
  }));

  for (const server of mcpServers) {
    const unavailable = server.discoveryStatus === 'unavailable' && server.tools.length === 0;
    const discoveredCount = server.tools.length > 0 ? server.tools.length : null;
    availableSources.push({
      kind: 'mcp_server',
      key: server.name,
      title: startCase(server.name),
      toolCount: discoveredCount,
      disabled: configuredKeys.has(`mcp_server:${server.name}`) || unavailable,
      unavailable,
    });
  }

  function updateEntry(index: number, patch: Partial<ToolSetEntryDraft>): void {
    onChange(entries.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
  }

  function addSource(kind: ToolSetEntryDraft['kind'], key: string): void {
    if (configuredKeys.has(`${kind}:${key}`)) return;
    onChange(
      insertEntryInAvailableSourceOrder(
        entries,
        {
          kind,
          key,
          disabledTools: [],
          deferredTools: [],
          deferred: false,
        },
        availableSources,
      ),
    );
  }

  function removeEntry(index: number): void {
    onChange(entries.filter((_, i) => i !== index));
  }

  if (loading) {
    return <Spinner className="tool-set-control-editor" />;
  }

  if (loadFailed) {
    return (
      <Alert className="tool-set-control-editor" severity="error">
        {t('toolCatalogLoadError')}
      </Alert>
    );
  }

  return (
    <Grid
      className="tool-set-control-editor"
      container
      spacing={4}
      sx={{
        margin: '14px 16px',
        alignItems: 'stretch',
      }}
    >
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('availableSourcesCount', { count: availableSources.length })}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            mb: 1,
          }}
        >
          {t('clickItemToAddHint')}
        </Typography>
        <Paper sx={{ overflow: 'auto' }}>
          {availableSources.length === 0 ? (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                px: 1,
              }}
            >
              {t('noSourcesAvailable')}
            </Typography>
          ) : (
            <MenuList dense disablePadding>
              {availableSources.map((source) => {
                return (
                  <MenuItem
                    key={`${source.kind}:${source.key}`}
                    disabled={source.disabled}
                    onClick={() => addSource(source.kind, source.key)}
                    sx={{
                      py: 2,
                      '& .MuiListItemIcon-root': {
                        minWidth: 'auto',
                        width: '1rem',
                        fontSize: '1rem',
                        '& svg': { fontSize: '1em' },
                      },
                    }}
                  >
                    <ListItemIcon>
                      <FaIconUvilo icon={getSourceIcon(source.kind, source.key, catalog)} square />
                    </ListItemIcon>
                    <Typography variant="subtitle2">{source.title}</Typography>
                    <Typography variant="body2" sx={{ ml: 2, color: 'text.disabled' }}>
                      {formatToolCount(source.toolCount)}
                    </Typography>
                    {source.unavailable ? (
                      <Chip
                        size="small"
                        label="Unavailable"
                        color="warning"
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                    ) : null}
                    <FaIconUvilo
                      icon="arrow-right"
                      sx={{
                        marginLeft: 'auto',
                        width: '24px',
                        textAlign: 'right',
                        opacity: source.disabled ? 0.4 : 1,
                      }}
                    />
                  </MenuItem>
                );
              })}
            </MenuList>
          )}
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ position: 'relative' }}>
          {t('configuredSourcesCount', { count: entries.length })}
          <PowerIconButton
            icon="arrow-rotate-right"
            title={t('refreshTools')}
            aria-label={t('refreshTools')}
            size="small"
            padding={3}
            loading={refreshing}
            onClick={() => void loadCatalog(true)}
            tooltipSx={{ position: 'absolute', right: 0, top: -8 }}
          />
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            mb: 1,
          }}
        >
          {entries.length === 0
            ? 'No sources yet. Pick from the available sources on the left.'
            : 'Expand a source to configure deferral and per-tool overrides.'}
        </Typography>
        {entries.length === 0 ? null : (
          <Box>
            {entries.map((entry, index) => {
              const mcpCatalog =
                entry.kind === 'mcp_server'
                  ? catalog?.mcpServers.find((s) => s.name === entry.key)
                  : undefined;
              const nativeGroup =
                entry.kind === 'native_group'
                  ? catalog?.nativeGroups.find((group) => group.key === entry.key)
                  : undefined;
              const groupTools =
                entry.kind === 'native_group'
                  ? (nativeGroup?.tools ?? [])
                  : (mcpCatalog?.tools ?? []);
              const displayName =
                entry.kind === 'native_group'
                  ? (nativeGroup?.label ?? startCase(entry.key))
                  : startCase(entry.key);
              const entryId = `${entry.kind}-${entry.key}`;
              // number of tools initially loaded
              const initialToolCount = entry.deferred
                ? 0
                : groupTools.filter(
                    (tool) =>
                      !entry.disabledTools.includes(tool) && !entry.deferredTools.includes(tool),
                  ).length;
              // number of deferred tools
              const deferredToolCount = entry.deferred
                ? groupTools.length - entry.disabledTools.length
                : entry.deferredTools.length;

              return (
                <React.Fragment key={entryId}>
                  <Accordion disableGutters id={`accordion-${entryId}`}>
                    <AccordionSummary
                      component="div"
                      expandIcon={<FaIconUvilo icon="chevron-down" />}
                      aria-controls={`details-${entryId}`}
                      id={`summary-${entryId}`}
                      sx={{
                        px: '16px',
                        '&:hover .hidden': { opacity: 1 },
                        '& .MuiAccordionSummary-content': {
                          margin: 0,
                        },
                        color: entry.deferred
                          ? 'warning.main'
                          : entry.deferredTools.length > 0
                            ? 'warning.dark'
                            : 'text.primary',
                      }}
                      onClick={(event: MouseEvent<HTMLElement>) => {
                        event.currentTarget.focus();
                      }}
                    >
                      <Grid
                        container
                        sx={{
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <Grid
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 4,
                          }}
                        >
                          <FaIconUvilo
                            icon={getSourceIcon(entry.kind, entry.key, catalog)}
                            square
                          />
                        </Grid>
                        <Grid sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle2">{displayName}</Typography>
                          <Tooltip
                            title={
                              <Box>
                                Available: {groupTools.length} tools
                                <br />
                                {t('initiallyLoadedCount', { count: initialToolCount })}
                                <br />
                                Deferred: {deferredToolCount}
                                <br />
                              </Box>
                            }
                          >
                            <Typography variant="body2" sx={{ ml: 2, color: 'text.disabled' }}>
                              {initialToolCount} + {deferredToolCount} of {groupTools.length}
                            </Typography>
                          </Tooltip>
                        </Grid>
                        <Grid
                          className="hidden"
                          container
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            position: 'absolute',
                            right: 50,
                            top: 0,
                            bottom: 0,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                          }}
                        >
                          <PowerIconButton
                            icon="circle-minus"
                            title={t('removeSource')}
                            aria-label={`Remove ${displayName}`}
                            size="small"
                            padding={3}
                            onClick={(event) => {
                              event.stopPropagation();
                              removeEntry(index);
                            }}
                          />
                          <PowerIconButton
                            icon="clock"
                            title={t('deferAllTools')}
                            aria-label={`Defer all tools from ${displayName}`}
                            size="small"
                            color={entry.deferred ? 'warning' : 'default'}
                            padding={3}
                            onClick={(event) => {
                              event.stopPropagation();
                              updateEntry(index, { deferred: !entry.deferred });
                            }}
                          />
                        </Grid>
                      </Grid>
                    </AccordionSummary>
                    <AccordionDetails id={`details-${entryId}`} sx={{ px: 0, pt: 0 }}>
                      {entry.kind === 'mcp_server' && mcpCatalog?.discoveryStatus === 'error' ? (
                        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
                          {mcpCatalog.discoveryError ?? discoveryStatusCaption('error')}
                        </Alert>
                      ) : null}
                      {entry.kind === 'mcp_server' &&
                      mcpCatalog?.discoveryStatus === 'unavailable' ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            display: 'block',
                            px: 2,
                            pb: 1,
                          }}
                        >
                          {discoveryStatusCaption('unavailable')}
                        </Typography>
                      ) : null}
                      {groupTools.length > 0 ? (
                        <Box component="ul" sx={{ m: 0, pl: 2, listStyle: 'none' }}>
                          {groupTools.map((toolKey) => (
                            <ToolOverrideRow
                              key={toolKey}
                              toolKey={toolKey}
                              toolLabel={startCase(toolKey)}
                              entry={entry}
                              entryDeferred={entry.deferred}
                              onToggleDisabled={(key, disabled) => {
                                const disabledTools = !disabled
                                  ? [...entry.disabledTools, key]
                                  : entry.disabledTools.filter((k) => k !== key);
                                updateEntry(index, { disabledTools });
                              }}
                              onToggleDeferred={(key, deferred) => {
                                const deferredTools = !deferred
                                  ? [...entry.deferredTools, key]
                                  : entry.deferredTools.filter((k) => k !== key);
                                updateEntry(index, { deferredTools });
                              }}
                            />
                          ))}
                        </Box>
                      ) : entry.kind === 'mcp_server' ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            px: 2,
                          }}
                        >
                          {t('noToolsInCache')}
                        </Typography>
                      ) : null}
                    </AccordionDetails>
                  </Accordion>
                  {index < entries.length - 1 ? <Divider /> : null}
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
